/** CreateMeetingModal — manual meeting creation / platform booking form.
 *
 * 两种模式：
 * - 手动登记：本地记录（原行为不变）；
 * - 预订腾讯会议：显式写操作，经 POST /meetings/book 在平台上真实创建会议，
 *   成功后提示会议号与入会链接。 */

import { useState } from "react";
import { DatePicker, Form, Input, Modal, Radio, Select, message } from "antd";
import { useBookMeeting, useCreateMeeting } from "../../api/queries";
import { MEETING_SOURCE_OPTIONS, type MeetingSource } from "../../types/meeting";

const { TextArea } = Input;

type CreateMode = "manual" | "book";

interface CreateMeetingForm {
  mode: CreateMode;
  title: string;
  source: MeetingSource;
  startsAt?: string;
  endsAt?: string;
  locationOrUrl?: string;
  organizerEmail?: string;
  participantsText?: string;
}

interface CreateMeetingModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (meetingId: string) => void;
}

export default function CreateMeetingModal({ open, onClose, onCreated }: CreateMeetingModalProps) {
  const [form] = Form.useForm<CreateMeetingForm>();
  const createMut = useCreateMeeting();
  const bookMut = useBookMeeting();
  const [messageApi, contextHolder] = message.useMessage();
  const [mode, setMode] = useState<CreateMode>("manual");
  const submitting = createMut.isPending || bookMut.isPending;

  const handleSubmit = async () => {
    let values: CreateMeetingForm;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    if (values.mode === "book") {
      if (!values.startsAt || !values.endsAt) {
        messageApi.error("预订会议需要填写开始与结束时间");
        return;
      }
      try {
        const res = await bookMut.mutateAsync({
          source: values.source === "manual" ? "tencent" : values.source,
          subject: values.title.trim(),
          startsAt: new Date(values.startsAt),
          endsAt: new Date(values.endsAt),
        });
        messageApi.success(
          res.data.locationOrUrl
            ? "会议已预订，入会链接见会议详情"
            : "会议已预订，请到腾讯会议客户端查看入会信息",
        );
        form.resetFields();
        onClose();
        onCreated?.(res.data.id);
      } catch (err) {
        // ApiError.message 来自后端统一错误信封（如"尚未绑定 tencent 会议账号"）
        const reason = err instanceof Error && err.message && !err.message.startsWith("API error")
          ? err.message
          : null;
        messageApi.error(reason ? `预订失败：${reason}` : "预订失败，请稍后重试");
      }
      return;
    }

    const participants = (values.participantsText ?? "")
      .split(/[\n,，;；]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((emailOrName) => ({
        email: emailOrName.includes("@") ? emailOrName : undefined,
        name: emailOrName.includes("@") ? undefined : emailOrName,
      }));

    try {
      const res = await createMut.mutateAsync({
        source: values.source,
        title: values.title.trim(),
        startsAt: values.startsAt ?? null,
        endsAt: values.endsAt ?? null,
        locationOrUrl: values.locationOrUrl?.trim() || undefined,
        organizerEmail: values.organizerEmail?.trim() || undefined,
        participantEmails: participants.length > 0 ? participants : undefined,
      });
      messageApi.success("会议已创建");
      form.resetFields();
      onClose();
      onCreated?.(res.data.id);
    } catch {
      messageApi.error("创建失败，请稍后重试");
    }
  };

  return (
    <Modal
      title="新建会议"
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={submitting}
      okText={mode === "book" ? "预订" : "创建"}
      cancelText="取消"
      destroyOnHidden
    >
      {contextHolder}
      <Form form={form} layout="vertical" initialValues={{ mode: "manual", source: "manual" }}>
        <Form.Item name="mode" label="创建方式" data-testid="meeting-create-mode">
          <Radio.Group
            onChange={(e) => setMode(e.target.value as CreateMode)}
            options={[
              { value: "manual", label: "手动登记" },
              { value: "book", label: "预订腾讯会议" },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="title"
          label="主题"
          rules={[{ required: true, message: "请输入会议主题" }]}
        >
          <Input placeholder="例如：与甲方首次沟通会" maxLength={200} />
        </Form.Item>
        {mode === "manual" && (
          <Form.Item name="source" label="来源">
            <Select options={MEETING_SOURCE_OPTIONS} />
          </Form.Item>
        )}
        <Form.Item
          name="startsAt"
          label="开始时间"
          rules={mode === "book" ? [{ required: true, message: "预订会议需要开始时间" }] : undefined}
        >
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="endsAt"
          label="结束时间"
          rules={mode === "book" ? [{ required: true, message: "预订会议需要结束时间" }] : undefined}
        >
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>
        {mode === "book" ? (
          <Form.Item noStyle>
            <p
              style={{
                color: "var(--jerry-text-tertiary, #888)",
                fontSize: "var(--jerry-font-size-sm, 13px)",
                marginTop: 0,
              }}
            >
              将以你已绑定的腾讯会议账号在平台上创建会议（需先在设置中完成授权），成功后
              会议号与入会链接显示在会议详情中。
            </p>
          </Form.Item>
        ) : (
          <>
            <Form.Item name="locationOrUrl" label="地点 / 会议链接">
              <Input placeholder="线下地址或在线会议链接" maxLength={500} />
            </Form.Item>
            <Form.Item name="organizerEmail" label="主持人邮箱">
              <Input placeholder="organizer@example.com" maxLength={200} />
            </Form.Item>
            <Form.Item name="participantsText" label="参会人（按回车或逗号分隔）">
              <TextArea
                placeholder={"zhang@example.com\nli@example.com\n王律师"}
                autoSize={{ minRows: 2, maxRows: 5 }}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
}
